import { useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useGroupCount, groupTabLabel } from '../lib/groups';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { latestAttr, registryOptions, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { STATE_SELECT_OPTIONS } from '../lib/us-states';
import { useEntityPermission } from '../lib/usePermissions';
import { useContributedTabs } from '../lib/entityTabs';
import { formatDate } from '../lib/orgTimezone';
import { Badge, Button, Checkbox } from '../atoms';
import { Tabs, DataTable, StatCard, CreateEntityModal, InlineEditCard, CustomFieldsPanel, EntityContactsPanel, EntityGroupsPanel, EntityNotesPanel, DocumentUploadModal, FollowButton, ArchiveButton, LastUpdated, PropertyMapCard, GenerateEmailModal, Modal, PropertyAssignmentsCard, useToast, type UploadedDocument } from '../organisms';
import { EntityHeader } from '../shell';

/**
 * PropertyDetail — /v2/properties/:id
 *
 * EntityHeader (prefix=location_code; title=name; status pill; KPI row)
 * above a Tabs panel: Overview · Parcels · Contacts · Documents.
 *
 * Phase 11 Block 8 PR 4 (2026-05-12).
 */

interface Property {
  id: string;
  name: string;
  client_id: string;
  location_code: string | null;
  file_code: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  status: 'active' | 'inactive' | 'in_setup' | 'historical' | 'sold' | 'sold_after_value' | 'unknown';
  re_pp: 'real_estate' | 'personal_property' | 'real_and_personal';
  category: string | null;
  prop_type: string | null;
  sub_type: string | null;
  year: number;
  year_built: number | null;
  land_sf: number | null;
  gba: number | null;
  nla: number | null;
  // migration 134 (mapping / aerial view) — the geocoded site point. Nullable:
  // un-geocoded is the normal state and the Map tab degrades to the address.
  latitude: number | null;
  longitude: number | null;
  updated_at?: string | null;
  updated_by?: string | null;
  archived_at?: string | null; // §9
}

interface Parcel {
  id: string;
  account_number: string;
  name: string | null;
  status: string;
  re_pp: 'real_estate' | 'personal_property';
  under_appeal: boolean;
  current_market_value: number | null;
  proposed_market_value: number | null;
}

interface Client {
  id: string;
  name: string;
}

const statusVariant: Record<Property['status'], 'success' | 'warning' | 'neutral' | 'info'> = {
  active: 'success', inactive: 'neutral', in_setup: 'warning', historical: 'info',
  sold: 'neutral', sold_after_value: 'neutral', unknown: 'neutral',
};

export function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Edition-contributed tabs (e.g. AppealStack Law's read-only "Matter" indicator). Empty for
  // a core install, so the tab list, valid set, and panel switch below are unchanged when off.
  const contributed = useContributedTabs('properties', { propertyId: id ?? '' });
  const [tab, setTab] = useTabState(['overview', 'map', 'parcels', 'contacts', 'groups', 'notes', 'billing', 'documents', 'custom', ...contributed.values], 'overview');
  const groupCount = useGroupCount('property', id);
  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // The email templates gate on the 'notification' entity like the savings email.
  const canGenerateEmail = useEntityPermission('notification', 'can_add');

  const propertyQuery = useQuery<Property>({
    queryKey: ['property', id],
    queryFn: ({ signal }) => api.get<Property>(`/api/properties/${id}`, signal),
    enabled: !!id,
  });

  const clientQuery = useQuery<Client>({
    queryKey: ['client', propertyQuery.data?.client_id],
    queryFn: ({ signal }) => api.get<Client>(`/api/clients/${propertyQuery.data!.client_id}`, signal),
    enabled: !!propertyQuery.data?.client_id,
  });

  const parcelsQuery = useQuery<Parcel[]>({
    queryKey: ['parcels-by-property', id],
    queryFn: ({ signal }) => api.get<Parcel[]>(`/api/parcels?property_id=${id}`, signal),
    enabled: !!id,
  });

  if (propertyQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading property…</p>;
  }

  if (propertyQuery.error) {
    const notFound = propertyQuery.error instanceof ApiClientError && propertyQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Property not found' : 'Failed to load property'}</h2>
        <p style={{ margin: 0 }}>{(propertyQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/properties')}>
            Back to Properties
          </Button>
        </p>
      </div>
    );
  }

  if (!propertyQuery.data) return null;

  const property = propertyQuery.data;
  const parcels = parcelsQuery.data ?? [];
  const totalCurrentValue = parcels.reduce((sum, p) => sum + (p.current_market_value || 0), 0);
  const parcelsUnderAppeal = parcels.filter(p => p.under_appeal).length;

  return (
    <>
      <EntityHeader
        prefix={property.location_code || undefined}
        title={property.name}
        subtitle={<>{[property.address_line1, property.city, property.state].filter(Boolean).join(', ') || null}<LastUpdated updatedBy={property.updated_by} updatedAt={property.updated_at} /></>}
        statusPills={<>
          <Badge variant={statusVariant[property.status]} dot>{property.status.replace(/_/g, ' ')}</Badge>
          {property.archived_at && <Badge variant="neutral">Archived</Badge>}
        </>}
        actions={
          <>
            {/* A3 — the box-level Edit controls live on each overview section; this
                fallback opens the full-record form so nothing is unreachable. */}
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
              Edit all fields
            </Button>
            {canGenerateEmail && (
              <Button variant="secondary" size="sm" onClick={() => setEmailOpen(true)}>
                Generate email
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => navigate(`/parcels/new?property_id=${property.id}`)}>
              New parcel
            </Button>
            <FollowButton entityType="property" entityId={property.id} />
            {/* §9 — archive / unarchive (super-admin + office administrator). */}
            <ArchiveButton
              entityType="properties"
              entityId={property.id}
              archived={!!property.archived_at}
              invalidateKeys={[['property', property.id], ['client-view', property.client_id], ['properties-list'], ['parcels-list']]}
            />
          </>
        }
        kpis={
          <>
            <StatCard label="Parcels" value={String(parcels.length)} caption="on this property" accent="hunter" />
            <StatCard
              label="Current value"
              value={totalCurrentValue > 0 ? `$${totalCurrentValue.toLocaleString()}` : '—'}
              caption="sum across parcels"
              accent="info"
            />
            <StatCard
              label="Under appeal"
              value={String(parcelsUnderAppeal)}
              caption={parcelsUnderAppeal > 0 ? `${parcelsUnderAppeal} of ${parcels.length} parcels` : 'No active appeals'}
              accent="amber"
            />
            <StatCard
              label="Building area"
              value={property.gba != null ? `${property.gba.toLocaleString()} sqft` : '—'}
              caption={property.year_built ? `Built ${property.year_built}` : 'No year on file'}
              accent="green"
            />
          </>
        }
      />

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { value: 'overview', label: 'Overview' },
          { value: 'map', label: 'Map' },
          { value: 'parcels', label: 'Parcels', count: parcels.length },
          { value: 'contacts', label: 'Contacts' },
          { value: 'groups', label: groupTabLabel(groupCount) },
          { value: 'notes', label: 'Notes' },
          { value: 'billing', label: 'Billing' },
          { value: 'documents', label: 'Documents' },
          { value: 'custom', label: 'Custom Fields' },
          ...contributed.tabItems,
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'overview' && (
          <OverviewTab
            property={property}
            clientName={clientQuery.data?.name ?? '—'}
            onClickClient={() => navigate(`/clients/${property.client_id}`)}
          />
        )}
        {tab === 'map' && <MapTab property={property} />}
        {tab === 'parcels' && (
          <ParcelsTab
            parcels={parcels}
            loading={parcelsQuery.isLoading}
            onRowClick={(p) => navigate(`/parcels/${p.id}`)}
          />
        )}
        {tab === 'contacts' && <EntityContactsPanel entityType="properties" entityId={property.id} />}
        {tab === 'groups' && <EntityGroupsPanel entityType="property" entityId={property.id} />}
        {tab === 'notes' && <EntityNotesPanel entityType="properties" entityId={property.id} />}
        {tab === 'billing' && <PropertyBillingTab propertyId={property.id} clientId={property.client_id} /> }
        {tab === 'documents' && <PropertyDocumentsTab propertyId={property.id} clientId={property.client_id} />}
        {tab === 'custom' && <CustomFieldsPanel entityType="properties" entityId={property.id} />}
        {contributed.panels[tab]}
      </div>

      <GenerateEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        scope={{ entityType: 'clients', entityId: property.client_id, clientId: property.client_id, propertyId: property.id, label: property.name }}
      />

      <CreateEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        table="properties"
        recordId={property.id}
        invalidateKeys={[['properties-list'], ['property', property.id]]}
      />
    </>
  );
}

/**
 * MapTab — the Pepco Jul-15 ask ("a Google Maps aerial view at the property
 * level"). A separate tab rather than an Overview card so the Overview grid is
 * untouched: the map wants full width and the imagery would otherwise
 * dominate a row of 280px info cards.
 *
 * The permission hook is declared HERE, in the component that owns the control,
 * and before any early return (the Rules-of-Hooks trap this repo has hit twice).
 */
function MapTab({ property }: { property: Property }) {
  const canEditProperty = useEntityPermission('property', 'can_edit');
  return (
    <PropertyMapCard
      propertyId={property.id}
      name={property.name}
      addressLine1={property.address_line1}
      city={property.city}
      state={property.state}
      postalCode={property.postal_code}
      latitude={property.latitude}
      longitude={property.longitude}
      canEdit={canEditProperty}
      invalidateKeys={[['property', property.id], ['properties-list']]}
    />
  );
}

function OverviewTab({
  property, clientName, onClickClient,
}: {
  property: Property;
  clientName: string;
  onClickClient: () => void;
}) {
  // A1/A5 — per-FIELD attribution so each box derives its own "last updated by · when".
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'properties', property.id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/properties/${property.id}`, signal),
  });
  const attr = attrQuery.data ?? {};
  const record = property as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [['property', property.id], ['properties-list'], ['field-attribution', 'properties', property.id]];
  // Inline-edit cards default to editable; gate on property.can_edit so a view-only role
  // (training) / agent can't edit. Hook — before the return.
  const canEditProperty = useEntityPermission('property', 'can_edit');

  // A8 — RE/PP + Property Type are org-configurable dropdowns (Part E).
  const propTypes = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'property-types'], queryFn: ({ signal }) => api.get('/api/org-settings/property-types', signal), staleTime: 5 * 60 * 1000 });
  const classifications = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'property-classifications'], queryFn: ({ signal }) => api.get('/api/org-settings/property-classifications', signal), staleTime: 5 * 60 * 1000 });
  const sqft = (v: unknown) => (v != null && v !== '' ? `${Number(v).toLocaleString()} sqft` : '—');

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'var(--sp-4)',
    }}>
      <InlineEditCard
        title="Identity" routePath="properties" recordId={property.id} record={record} canEdit={canEditProperty} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['name', 'location_code', 'file_code'])}
        fields={[
          { key: 'name', label: 'Name', required: true },
          { key: 'location_code', label: 'Location code' },
          { key: 'file_code', label: 'File code' },
        ]}
      />
      <InlineEditCard
        title="Address" routePath="properties" recordId={property.id} record={record} canEdit={canEditProperty} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['address_line1', 'city', 'state', 'postal_code', 'year'])}
        fields={[
          { key: 'address_line1', label: 'Street' },
          { key: 'city', label: 'City' },
          // A8 — State + ZIP on SEPARATE lines (each field is its own row); State a
          // dropdown of US codes; ZIP enforces number format.
          { key: 'state', label: 'State', type: 'select', options: STATE_SELECT_OPTIONS.map((o) => ({ value: String(o.value), label: o.label })) },
          { key: 'postal_code', label: 'ZIP', type: 'number' },
          { key: 'year', label: 'Tax year', type: 'number' },
        ]}
      />
      <InlineEditCard
        title="Classification" routePath="properties" recordId={property.id} record={record} canEdit={canEditProperty} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['re_pp', 'category', 'prop_type', 'sub_type'])}
        fields={[
          { key: 're_pp', label: 'RE/PP', type: 'select', options: registryOptions(classifications.data), addOptionSlug: 'property-classifications', display: (v) => (v ? humanizeToken(String(v)) : '—') },
          { key: 'category', label: 'Category' },
          { key: 'prop_type', label: 'Type', type: 'select', options: registryOptions(propTypes.data), addOptionSlug: 'property-types', display: (v) => (v ? humanizeToken(String(v)) : '—') },
          { key: 'sub_type', label: 'Sub-type' },
        ]}
      />
      <InlineEditCard
        title="Dimensions" routePath="properties" recordId={property.id} record={record} canEdit={canEditProperty} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['land_sf', 'gba', 'nla', 'year_built'])}
        fields={[
          // A8 — dimension fields enforce number format on edit.
          { key: 'land_sf', label: 'Land (sqft)', type: 'number', display: sqft },
          { key: 'gba', label: 'GBA (sqft)', type: 'number', display: sqft },
          { key: 'nla', label: 'NLA (sqft)', type: 'number', display: sqft },
          { key: 'year_built', label: 'Year built', type: 'number' },
        ]}
      />
      {/* ATA part 3 §2: assignments live on the property — Originator, Manager, Consultant, Assistant, two
          slots each, the client team showing through as the default. */}
      <BillingReadinessBanner propertyId={property.id} />
      <PropertyAssignmentsCard propertyId={property.id} clientId={property.client_id} canEdit={canEditProperty} invalidateKeys={invalidate} />
      <PropertyCommissionSplitCard propertyId={property.id} />
      {/* 196 (ATA part 4 §4a): what the client paid and reports, so the appeal recommendation can
          quote % of purchase price and the imputed cap rate; the legal owner the refund form names. */}
      <InlineEditCard
        title="Acquisition" routePath="properties" recordId={property.id} record={record} canEdit={canEditProperty} invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['purchase_price', 'purchase_date', 'reported_noi', 'reported_noi_year', 'reported_income', 'reported_expenses', 'legal_owner_name', 'tracking_only', 'tracking_only_scope'])}
        fields={[
          { key: 'purchase_price', label: 'Purchase price', type: 'number', display: (v) => (v == null || v === '' ? '—' : `$${Number(v).toLocaleString()}`) },
          { key: 'purchase_date', label: 'Purchase date', type: 'date' },
          { key: 'reported_noi', label: 'Reported NOI', type: 'number', display: (v) => (v == null || v === '' ? '—' : `$${Number(v).toLocaleString()}`) },
          { key: 'reported_noi_year', label: 'NOI year', type: 'number' },
          { key: 'reported_income', label: 'Reported income', type: 'number', display: (v) => (v == null || v === '' ? '—' : `${Number(v).toLocaleString()}`) },
          { key: 'reported_expenses', label: 'Reported expenses', type: 'number', display: (v) => (v == null || v === '' ? '—' : `${Number(v).toLocaleString()}`) },
          { key: 'legal_owner_name', label: 'Legal owner' },
          { key: 'tracking_only', label: 'Tracking only', type: 'checkbox' },
          { key: 'tracking_only_scope', label: 'Tracking only through', type: 'select', options: [{ value: 'all', label: 'Every stage (never bill)' }, { value: 'through_arb', label: 'Through ARB (bill from litigation)' }], display: (v) => (v === 'through_arb' ? 'Through ARB, bill from litigation' : 'Every stage') },
        ]}
      />
      {/* The owning client — read-only link (a property isn't reassigned here). */}
      <div style={{ gridColumn: '1 / -1', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
        Client:{' '}
        <a onClick={onClickClient} style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: 'var(--fw-medium)' }}>{clientName} ↗</a>
      </div>
    </div>
  );
}

function ParcelsTab({
  parcels, loading, onRowClick,
}: {
  parcels: Parcel[];
  loading: boolean;
  onRowClick: (p: Parcel) => void;
}) {
  const columns = useMemo<ColumnDef<Parcel>[]>(() => [
    {
      accessorKey: 'account_number',
      header: 'Account #',
      cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)' }}>{getValue() as string}</span>,
    },
    { accessorKey: 'name', header: 'Name', cell: ({ getValue }) => (getValue() as string) || '—' },
    {
      accessorKey: 're_pp',
      header: 'Type',
      cell: ({ getValue }) => (getValue() === 'real_estate' ? 'Real estate' : 'Personal property'),
    },
    {
      accessorKey: 'current_market_value',
      header: 'Current value',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
    {
      accessorKey: 'under_appeal',
      header: 'Appeal',
      cell: ({ getValue }) => (
        (getValue() as boolean)
          ? <Badge variant="warning" dot>Under appeal</Badge>
          : <span style={{ color: 'var(--color-ink-3)' }}>—</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => <Badge variant="neutral">{((getValue() as string | null) ?? '').replace(/_/g, ' ') || '—'}</Badge>,
    },
  ], []);
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading parcels…</p>;
  if (parcels.length === 0) return <Empty message="No parcels on this property yet. Click 'New parcel' to add one." />;
  return <DataTable data={parcels} columns={columns} onRowClick={onRowClick} />;
}

interface PropertyDoc {
  id: string;
  name: string;
  document_type: string | null;
  year: number | null;
  status: string | null;
}

/** Property-level documents. Adding here stamps this property (and, via the
 *  upload route, the owning client) so the doc rolls up to the client's All
 *  Documents automatically. */
const isOperatingStatement = (type: string | null | undefined) => /^operating statements?$/i.test(String(type || '').trim());

function PropertyDocumentsTab({ propertyId, clientId }: { propertyId: string; clientId: string }) {
  const { toast } = useToast();
  const [uploadOpen, setUploadOpen] = useState(false);
  // ATA part 4 §5: an uploaded Operating Statement offers its figures; the dialog is the
  // confirmation click, and it can be reopened from the row for any statement on file.
  const [statementDocId, setStatementDocId] = useState<string | null>(null);
  const canEditProperty = useEntityPermission('property', 'can_edit');
  const docsQuery = useQuery<PropertyDoc[]>({
    queryKey: ['documents', 'property', propertyId],
    queryFn: ({ signal }) => api.get<PropertyDoc[]>(`/api/documents?property_id=${propertyId}`, signal),
    enabled: !!propertyId,
  });
  const columns = useMemo<ColumnDef<PropertyDoc>[]>(() => [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'document_type', header: 'Type', cell: ({ getValue }) => { const v = getValue() as string | null; return v ? <Badge variant="neutral">{v}</Badge> : '—'; } },
    { accessorKey: 'year', header: 'Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{(getValue() as string) || 'Received'}</Badge> },
    { id: 'download', header: '', cell: ({ row }) => (
      <span style={{ display: 'inline-flex', gap: 'var(--sp-2)' }}>
        {canEditProperty && isOperatingStatement(row.original.document_type) && (
          <Button variant="secondary" size="sm" onClick={() => setStatementDocId(row.original.id)}>Apply figures</Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => downloadDocument(row.original.id, (m) => toast({ variant: 'error', message: m }))}>Download</Button>
      </span>
    ) },
  ], [toast, canEditProperty]);
  const docs = docsQuery.data ?? [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          Documents added here belong to this property and roll up to the client's All Documents automatically.
        </p>
        <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>Upload document</Button>
      </div>
      {docsQuery.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading documents…</p>
      ) : docsQuery.error ? (
        <p style={{ color: 'var(--color-status-red)' }}>
          Couldn’t load documents — {(docsQuery.error as Error)?.message || 'please try again'}.
        </p>
      ) : docs.length === 0 ? (
        <Empty message="No documents on this property yet. Use “Upload document” to add one." />
      ) : (
        <DataTable data={docs} columns={columns} />
      )}
      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultContext={{ property_id: propertyId, client_id: clientId }}
        onUploaded={(uploaded: UploadedDocument[]) => {
          const statement = uploaded.find((d) => isOperatingStatement(d.document_type) && d.extracted);
          if (statement && canEditProperty) setStatementDocId(statement.id);
        }}
      />
      {statementDocId && (
        <OperatingStatementDialog documentId={statementDocId} propertyId={propertyId} onClose={() => setStatementDocId(null)} />
      )}
    </div>
  );
}

interface StatementReading {
  document: { id: string; name: string; filename: string; year: number | null; property_id: string | null };
  extracted: {
    property_name: string | null; units: number | null; statement_year: number | null; tax_year: number | null;
    reported_income?: number | null; reported_expenses?: number | null; reported_noi?: number | null; expense_ratio?: number | null;
    warnings: string[];
  };
  property: { id: string; name: string; units: number | null; reported_noi?: number | null; reported_noi_year: number | null; reported_income?: number | null; reported_expenses?: number | null } | null;
  can_apply_money: boolean;
}

const usd = (v: number | null | undefined) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

/** The confirmation step (ATA part 4 §5): the figures read from the workbook beside what the
 *  property holds today; only the ticked figures are written, on Apply. */
function OperatingStatementDialog({ documentId, propertyId, onClose }: { documentId: string; propertyId: string; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const reading = useQuery<StatementReading>({
    queryKey: ['operating-statement', documentId],
    queryFn: ({ signal }) => api.get<StatementReading>(`/api/operating-statements/${documentId}`, signal),
  });
  const [applyUnits, setApplyUnits] = useState(true);
  const [applyNoi, setApplyNoi] = useState(true);
  const data = reading.data;
  const ex = data?.extracted;
  const unitsOffered = ex?.units != null;
  const noiOffered = !!data?.can_apply_money && ex?.reported_noi != null;
  const apply = useMutation({
    mutationFn: async () => {
      const fields: Record<string, number | null> = {};
      if (unitsOffered && applyUnits) fields.units = ex!.units;
      if (noiOffered && applyNoi) {
        fields.reported_noi = ex!.reported_noi!;
        if (ex!.statement_year != null) fields.reported_noi_year = ex!.statement_year;
        if (ex!.reported_income != null) fields.reported_income = ex!.reported_income;
        if (ex!.reported_expenses != null) fields.reported_expenses = ex!.reported_expenses;
      }
      return api.post<{ applied: string[] }>(`/api/operating-statements/${documentId}/apply`, { fields });
    },
    onSuccess: (res) => {
      toast({ variant: 'success', message: res.applied.length ? `Applied ${res.applied.map((f) => f.replace(/_/g, ' ')).join(', ')} to the property.` : 'The property already held these figures.' });
      queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['properties-list'] });
      queryClient.invalidateQueries({ queryKey: ['field-attribution', 'properties', propertyId] });
      onClose();
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/, '') : 'Could not apply the figures' }),
  });
  const nothingTicked = !(unitsOffered && applyUnits) && !(noiOffered && applyNoi);
  return (
    <Modal
      open
      onClose={onClose}
      title="Operating statement figures"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => apply.mutate()} disabled={!data || nothingTicked} loading={apply.isPending}>Apply to property</Button>
        </>
      )}
    >
      {reading.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Reading the workbook…</p>
      ) : reading.error || !data ? (
        <p style={{ color: 'var(--color-status-red)' }}>Couldn’t read this statement — {(reading.error as Error)?.message?.replace(/^HTTP \d+:\s*/, '') || 'please try again'}.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
            Read from <strong>{data.document.name}</strong>{ex?.property_name ? ` (${ex.property_name}` + (ex.statement_year ? `, ${ex.statement_year} operating year)` : ')') : ''}. Nothing is written until you click Apply.
          </p>
          <table style={{ borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
            <thead><tr>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Figure</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>In the statement</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>On the property now</th>
            </tr></thead>
            <tbody>
              <tr><td style={{ padding: '4px 8px' }}>Unit count</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{ex?.units ?? '—'}</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{data.property?.units ?? '—'}</td></tr>
              {data.can_apply_money && (
                <>
                  <tr><td style={{ padding: '4px 8px' }}>Income</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{usd(ex?.reported_income)}</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{usd(data.property?.reported_income)}</td></tr>
                  <tr><td style={{ padding: '4px 8px' }}>Expenses</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{usd(ex?.reported_expenses)}</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{usd(data.property?.reported_expenses)}</td></tr>
                  <tr><td style={{ padding: '4px 8px' }}>NOI</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{usd(ex?.reported_noi)}</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{usd(data.property?.reported_noi)}{data.property?.reported_noi_year ? ` (${data.property.reported_noi_year})` : ''}</td></tr>
                  <tr><td style={{ padding: '4px 8px' }}>Expense ratio</td><td style={{ textAlign: 'right', padding: '4px 8px' }}>{ex?.expense_ratio != null ? `${ex.expense_ratio}%` : '—'}</td><td style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--color-ink-3)' }}>derived</td></tr>
                </>
              )}
            </tbody>
          </table>
          {ex?.warnings?.length ? (
            <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)', color: 'var(--color-status-amber, #92400e)', fontSize: 'var(--fs-sm)' }}>
              {ex.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {unitsOffered && <Checkbox label={`Set the unit count to ${ex!.units}`} checked={applyUnits} onChange={(e) => setApplyUnits(e.target.checked)} />}
            {noiOffered && (
              <Checkbox
                label={`Set the reported NOI to ${usd(ex!.reported_noi)}${ex!.statement_year ? ` for ${ex!.statement_year}` : ''}`}
                description="Also records the income and expenses the NOI was derived from, so the expense ratio can be quoted."
                checked={applyNoi}
                onChange={(e) => setApplyNoi(e.target.checked)}
              />
            )}
            {!unitsOffered && !noiOffered && <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Nothing in this workbook can be applied{data.can_apply_money ? '' : ' by your role'}.</p>}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}

// ── A16 — the property's own Billing tab ────────────────────────────────────
// Yadira: *"we don't have a billing tab on the property profiles… We definitely
// need to add a billing tab to the property profiles. Those invoices will just be
// for those specific properties, and everything for that client will live at that
// level."* So this is the client Billing tab narrowed to this one property, with a
// standing link up to the client level — the property view is a filter, and the
// client profile stays the place the whole book is reconciled.
//
// It is READ-WRITE as of Q1 (round 5): asked whether an invoice may be cut
// per-property rather than per-client / per-owner, the answer was "per property
// invoicing is real — a biller should be able to invoice one property on its own."
// So property is a third invoicing grain and the action belongs here. It posts to
// the same generateInvoice spine the client batch uses, so an item leaves
// 'billable' by exactly one path no matter which grain the biller picked.

interface PropertyBillable {
  id: string;
  parcel_account: string | null;
  parcel_name: string | null;
  year: number | null;
  status: string | null;
  billing_type: string | null;
  amount: number | null;
}

interface PropertyInvoice {
  id: string;
  invoice_number: string | null;
  status: string | null;
  invoice_date: string | null;
  amount: number | null;
  balance: number | null;
}

const propertyMoney = (v: number | null | undefined) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '—';

function PropertyBillingTab({ propertyId, clientId }: { propertyId: string; clientId: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // The endpoint is `processGate(req, res, 'invoice')` (routes/workflows.js:501), so the
  // control must gate on can_PROCESS. Gating on can_add reproduced the exact FE/BE verb
  // mismatch the client twin was already fixed for: a role with add-but-not-process saw a
  // live button that 403s, and a role with process-but-not-add had the action hidden.
  const canInvoice = useEntityPermission('invoice', 'can_process');
  const query = useQuery<{ billables: PropertyBillable[]; invoices: PropertyInvoice[] }>({
    queryKey: ['property-billing', propertyId],
    queryFn: ({ signal }) => api.get(`/api/property-billing/${propertyId}`, signal),
  });

  const invoiceProperty = useMutation<{ invoiceId?: string; invoiceNumber?: string }, Error, void>({
    mutationFn: () => api.post(`/api/workflow/invoice-all-for-property/${propertyId}`, {}),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['property-billing', propertyId] });
      // The client's own Billing tab reads the SAME rows through two different keys, and
      // invalidating one is not enough: ['client-invoices'] is the invoice list, but the
      // "ready to invoice" list is ['billables', clientId] (ClientDetail.tsx:637). Without
      // the second, items just invoiced keep showing as still-billable on the client tab —
      // inviting someone to invoice them again.
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: ['client-invoices', clientId] });
        queryClient.invalidateQueries({ queryKey: ['billables', clientId] });
      }
      toast({ variant: 'success', message: res.invoiceNumber ? `Invoice ${res.invoiceNumber} created.` : 'Invoice created.' });
      if (res.invoiceId) navigate(`/invoices/${res.invoiceId}`);
    },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || e.message });
    },
  });

  if (query.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading billing…</p>;
  if (query.error) {
    // A role without invoice_item view (some agents/analysts) must read as a clean
    // "restricted" state, not a red error box — mirrors the client Billing tab.
    const msg = (query.error as Error).message;
    const restricted = /permission denied/i.test(msg);
    return (
      <div style={{
        padding: 'var(--sp-4)',
        background: restricted ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: `1px solid ${restricted ? 'var(--color-border)' : 'var(--color-status-red)'}`,
        color: restricted ? 'var(--color-ink-3)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>{restricted ? 'Billing details are restricted for your role.' : msg}</div>
    );
  }

  const billables = query.data?.billables ?? [];
  const invoices = query.data?.invoices ?? [];
  // Money-blind roles get every amount nulled server-side, so a total of 0 would
  // read as "nothing to bill" rather than "you may not see this". Show a dash.
  const anyAmount = billables.some((b) => b.amount != null);
  const readyTotal = billables.reduce((sum, b) => sum + (b.amount ?? 0), 0);

  const billableColumns: ColumnDef<PropertyBillable>[] = [
    { accessorKey: 'parcel_account', header: 'Account',
      cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)' }}>{(getValue() as string | null) ?? '—'}</span> },
    { accessorKey: 'parcel_name', header: 'Parcel', cell: ({ getValue }) => (getValue() as string | null) ?? '—' },
    { accessorKey: 'year', header: 'Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'billing_type', header: 'Type',
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? <Badge variant="neutral">{v.replace(/_/g, ' ')}</Badge> : '—';
      } },
    { accessorKey: 'amount', header: 'Fee', cell: ({ getValue }) => propertyMoney(getValue() as number | null) },
  ];

  const invoiceColumns: ColumnDef<PropertyInvoice>[] = [
    { accessorKey: 'invoice_number', header: 'Invoice',
      cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)' }}>{(getValue() as string | null) ?? '—'}</span> },
    { accessorKey: 'invoice_date', header: 'Date', cell: ({ getValue }) => formatDate(getValue()) || '—' },
    { accessorKey: 'status', header: 'Status',
      cell: ({ getValue }) => {
        const v = (getValue() as string | null) ?? '';
        return v ? <Badge variant={v === 'paid' ? 'success' : 'neutral'}>{v.replace(/_/g, ' ')}</Badge> : '—';
      } },
    { accessorKey: 'amount', header: 'Amount', cell: ({ getValue }) => propertyMoney(getValue() as number | null) },
    { accessorKey: 'balance', header: 'Balance', cell: ({ getValue }) => propertyMoney(getValue() as number | null) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 'var(--sp-3)' }}>
        <StatCard label="Ready to invoice" value={anyAmount ? propertyMoney(readyTotal) : '—'} />
        <StatCard label="Billable items" value={String(billables.length)} />
        <StatCard label="Invoices" value={String(invoices.length)} />
      </div>

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
          <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-md)' }}>Ready to invoice</h3>
          {canInvoice && billables.length > 0 && (
            <Button variant="primary" size="sm" disabled={invoiceProperty.isPending}
              onClick={() => invoiceProperty.mutate()}>
              {invoiceProperty.isPending ? 'Invoicing…' : 'Invoice this property'}
            </Button>
          )}
        </div>
        {billables.length === 0
          ? <p style={{ color: 'var(--color-ink-3)' }}>Nothing staged for this property.</p>
          : <DataTable data={billables} columns={billableColumns} />}
      </section>

      <section>
        <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-md)' }}>Invoices for this property</h3>
        {invoices.length === 0
          ? <p style={{ color: 'var(--color-ink-3)' }}>No invoices carry a line for this property yet.</p>
          : <DataTable data={invoices} columns={invoiceColumns} onRowClick={(row) => navigate(`/invoices/${row.id}`)} />}
      </section>

      {clientId && (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          This tab covers this property only.{' '}
          <a href={`/clients/${clientId}?tab=billing`} style={{ color: 'var(--brand-primary)' }}>
            The client's full billing
          </a>{' '}covers every property in the book.
        </p>
      )}
    </div>
  );
}

/** The commission split this property derives today (ATA part 3 §3a/§3d): the people from its Assignments card
 *  at the firm's fixed role percents. Read-only, internal, never in a portal; what the next invoice will copy. */
function PropertyCommissionSplitCard({ propertyId }: { propertyId: string }) {
  const split = useQuery<{ rows: Array<{ role: string; role_label: string; slot: number; inherited: boolean; assignee_name: string; percent: number }>; unallocated: Array<{ role: string; label: string; percent: number }>; total: number }>({
    queryKey: ['commission-split-property', propertyId],
    queryFn: ({ signal }) => api.get(`/api/commissions/splits/property/${propertyId}`, signal),
    retry: false,
  });
  return (
    <div data-testid="property-commission-split" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Commission split</h3>
      {split.isLoading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
      ) : split.error || !split.data || !Array.isArray(split.data.rows) ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Not available for your role.</p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--sp-1)', fontSize: 'var(--fs-sm)' }}>
          {split.data.rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <span style={{ color: 'var(--color-ink-3)' }}>{r.role_label}{split.data!.rows.filter((x) => x.role === r.role).length > 1 ? ` slot ${r.slot}` : ''}</span>
              <span style={{ fontWeight: 'var(--fw-medium)' }}>{r.assignee_name} · {r.percent}%</span>
            </div>
          ))}
          {(split.data.unallocated ?? []).map((u) => (
            <div key={u.role} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', color: 'var(--color-status-red)' }}>
              <span>{u.label}: nobody assigned</span><span>{u.percent}% unallocated</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--sp-1)', fontWeight: 'var(--fw-medium)' }}>
            <span>Total</span><span>{split.data.total}%</span>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * ATA part 3 §5c: when the firm has billing fields block save, say what invoicing still needs on
 * this property (the client's Manager and Originator, a fee term for the year, Consultant slot 1).
 */
function BillingReadinessBanner({ propertyId }: { propertyId: string }) {
  const q = useQuery<{ enforced: boolean; ready: boolean; missing: Array<{ field: string; label: string; message: string }> }>({
    queryKey: ['billing-readiness', propertyId],
    queryFn: ({ signal }) => api.get(`/api/property-assignments/${propertyId}/billing-readiness`, signal),
    retry: false,
  });
  const d = q.data;
  if (!d || !d.enforced || d.ready || !Array.isArray(d.missing) || d.missing.length === 0) return null;
  return (
    <div role="alert" style={{
      padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)',
      background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', borderRadius: 'var(--r-lg)',
      color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)',
    }}>
      <div style={{ fontWeight: 'var(--fw-semibold)', marginBottom: 'var(--sp-1)' }}>Billing setup incomplete. This property cannot be invoiced until:</div>
      <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)' }}>
        {d.missing.map((m) => <li key={m.field}>{m.message}</li>)}
      </ul>
    </div>
  );
}
